/**
 * @description Hook for CMS site status on the Company > Website tab (LYN-21, LYN-20)
 *
 * @dependencies
 * - cmsService: GET /api/features/cms-site, domain connect endpoints
 * - useCompany: Re-fetch when active company changes
 *
 * @notes
 * Skips GET /cms-site when no company is selected or cms.view is missing
 * so Employees / first-time signup cannot produce "Unable to load website
 * status". Backend still denies cms-site* without cms.view.
 */

import { useState, useEffect, useCallback } from 'react';
import cmsService from '../services/api/cmsService';
import { useCompany } from '../contexts/CompanyContext';
import { usePermissions } from '../contexts/PermissionContext';
import { PERMISSIONS } from '../constants/permissionMappings';
import { getApiErrorMessage } from '../utils/apiErrorMessage';

/**
 * @returns {{
 *   loading: boolean,
 *   error: string|null,
 *   siteStatus: object|null,
 *   provisioning: boolean,
 *   provisionError: string|null,
 *   domainStatus: object|null,
 *   domainLoading: boolean,
 *   domainError: string|null,
 *   connectingDomain: boolean,
 *   connectDomainError: string|null,
 *   disconnectingDomain: boolean,
 *   disconnectDomainError: string|null,
 *   refresh: () => Promise<void>,
 *   provision: () => Promise<boolean>,
 *   refreshDomainStatus: () => Promise<void>,
 *   connectDomain: (domain: string) => Promise<boolean>,
 *   disconnectDomain: () => Promise<boolean>
 * }}
 */
export default function useCmsSite() {
  const { company } = useCompany();
  const { hasPermission, isLoaded } = usePermissions();
  const canViewCms = isLoaded && hasPermission(PERMISSIONS.cms.view);
  const [siteStatus, setSiteStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [provisioning, setProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState(null);
  const [domainStatus, setDomainStatus] = useState(null);
  const [domainLoading, setDomainLoading] = useState(false);
  const [domainError, setDomainError] = useState(null);
  const [connectingDomain, setConnectingDomain] = useState(false);
  const [connectDomainError, setConnectDomainError] = useState(null);
  const [disconnectingDomain, setDisconnectingDomain] = useState(false);
  const [disconnectDomainError, setDisconnectDomainError] = useState(null);

  const load = useCallback(async () => {
    if (!company?.id || !canViewCms) {
      setSiteStatus(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const status = await cmsService.getCmsSiteStatus();
      setSiteStatus(status);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load website status'));
      setSiteStatus(null);
    } finally {
      setLoading(false);
    }
  }, [company?.id, canViewCms]);

  useEffect(() => {
    load();
  }, [load]);

  const provision = useCallback(async () => {
    setProvisioning(true);
    setProvisionError(null);

    try {
      const status = await cmsService.provisionCmsSite();
      setSiteStatus(status);
      return true;
    } catch (err) {
      setProvisionError(getApiErrorMessage(err, 'Failed to set up your website'));
      return false;
    } finally {
      setProvisioning(false);
    }
  }, []);

  const refreshDomainStatus = useCallback(async () => {
    setDomainLoading(true);
    setDomainError(null);

    try {
      const status = await cmsService.getCmsDomainStatus();
      setDomainStatus(status);
      if (status.customDomain?.verified) {
        setSiteStatus((prev) => {
          if (!prev?.site) {
            return prev;
          }
          return {
            ...prev,
            site: {
              ...prev.site,
              customHostname: status.customDomain.hostname,
              customHostnameStatus: status.customDomain.status
            },
            urls: prev.urls
              ? {
                ...prev.urls,
                siteUrl: `https://${status.customDomain.hostname}`,
                usesCustomDomain: true
              }
              : prev.urls
          };
        });
      }
    } catch (err) {
      setDomainError(getApiErrorMessage(err, 'Failed to load domain status'));
    } finally {
      setDomainLoading(false);
    }
  }, []);

  const connectDomain = useCallback(async (domain) => {
    setConnectingDomain(true);
    setConnectDomainError(null);
    setDisconnectDomainError(null);

    try {
      const status = await cmsService.connectCmsDomain(domain);
      setDomainStatus(status);
      setSiteStatus((prev) => {
        if (!prev?.site) {
          return prev;
        }
        return {
          ...prev,
          site: {
            ...prev.site,
            customHostname: status.customDomain?.hostname || prev.site.customHostname,
            customHostnameStatus: status.customDomain?.status || prev.site.customHostnameStatus
          }
        };
      });
      return true;
    } catch (err) {
      setConnectDomainError(
        getApiErrorMessage(
          err,
          'We could not connect your domain. Please try again, or contact support if this continues.'
        )
      );
      return false;
    } finally {
      setConnectingDomain(false);
    }
  }, []);

  const disconnectDomain = useCallback(async () => {
    setDisconnectingDomain(true);
    setDisconnectDomainError(null);
    setConnectDomainError(null);

    try {
      const status = await cmsService.disconnectCmsDomain();
      setDomainStatus(status);
      setSiteStatus((prev) => {
        if (!prev?.site) {
          return prev;
        }
        return {
          ...prev,
          site: {
            ...prev.site,
            customHostname: status.customDomain?.hostname || null,
            customHostnameStatus: status.customDomain?.status || 'none'
          },
          urls: prev.urls
            ? {
              ...prev.urls,
              siteUrl: status.defaultUrl || prev.urls.siteUrl,
              usesCustomDomain: false
            }
            : prev.urls
        };
      });
      return true;
    } catch (err) {
      setDisconnectDomainError(getApiErrorMessage(err, 'Failed to disconnect domain'));
      return false;
    } finally {
      setDisconnectingDomain(false);
    }
  }, []);

  useEffect(() => {
    if (siteStatus?.provisioned) {
      refreshDomainStatus();
    } else {
      setDomainStatus(null);
      setDomainError(null);
      setConnectDomainError(null);
      setDisconnectDomainError(null);
    }
  }, [siteStatus?.provisioned, company?.id, refreshDomainStatus]);

  return {
    loading,
    error,
    siteStatus,
    provisioning,
    provisionError,
    domainStatus,
    domainLoading,
    domainError,
    connectingDomain,
    connectDomainError,
    disconnectingDomain,
    disconnectDomainError,
    refresh: load,
    provision,
    refreshDomainStatus,
    connectDomain,
    disconnectDomain
  };
}
